import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Tab } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";

import api from "api";

import ImpactStatementView from "./ImpactStatementView";
import { StageSegment } from "./StageSegments";
import { impactStageDefinitions, impactStageOrder } from "./statementUtils";

type Challenge = OpenAPI.GET<"/challenges/{id}">["response"];
type ImpactStatements =
  OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"];

type ImpactStatementProps = {
  impactStatements: ImpactStatements;
  setImpactStatements: Dispatch<SetStateAction<ImpactStatements>>;
  forStage?: OpenAPI.Schemas["ImpactStatement"]["stage"];
  statementDefinition: Challenge["impactStatements"];
  restrictStagesTo?: OpenAPI.Schemas["ImpactStatement"]["stage"][];
  ideaId: string;
  editable?: boolean;
  canManageIdea?: boolean;
};

export const ImpactStatementEditor = ({
  impactStatements,
  setImpactStatements,
  statementDefinition,
  forStage,
  restrictStagesTo: availableStages,
  ideaId,
  editable,
  canManageIdea,
}: ImpactStatementProps) => {
  const { t } = useTranslation();
  const existingStages = Object.keys(
    impactStatements,
  ) as OpenAPI.Schemas["ImpactStatement"]["stage"][];
  const [stage, setStage] = useState(forStage || existingStages[0]);
  const [isCreatingStage, setIsCreatingStage] = useState(null);

  const createImpactStatement = useCallback(
    (
      newStatement: ImpactStatements["userEstimate"],
      forStage: OpenAPI.Schemas["ImpactStatement"]["stage"],
    ) => {
      setIsCreatingStage(forStage);
      api.ideas.createImpactStatement(
        ideaId,
        {
          impacts: newStatement.impacts,
          description: newStatement.description,
          stage: forStage,
        },
        ({ impactStatement: newImpactStatement }) => {
          setIsCreatingStage(null);
          setImpactStatements({
            ...impactStatements,
            [forStage]: newImpactStatement,
          });
        },
        () => {
          setIsCreatingStage(null);
          toast.error(
            `Failed to save ${t("generic.impact")} statement. Please try again.`,
          );
          setImpactStatements({ ...impactStatements, [forStage]: undefined });
        },
      );
    },
    [ideaId, impactStatements, setImpactStatements, t],
  );

  const saveImpactStatement = useThrottle(
    (newStatement: ImpactStatements["userEstimate"]) => {
      api.ideas.updateImpactStatement(
        ideaId,
        newStatement._id,
        {
          impacts: newStatement.impacts,
          description: newStatement.description,
        },
        ({ impactStatement }) => {
          api.setQueryData(api.ideas.getImpactStatements, [ideaId], (prev) => ({
            ...prev,
            impactStatement,
          }));
        },
        () => {
          toast.error(
            `Failed to update ${t("generic.impact")} statement. Please try again.`,
          );
        },
      );
    },
    400,
    [ideaId],
  );

  const updateImpactStatement = useCallback(
    (newStatement: ImpactStatements["userEstimate"]) => {
      if (newStatement._id) {
        setImpactStatements({ ...impactStatements, [stage]: newStatement });
      }
      saveImpactStatement(newStatement);
    },
    [saveImpactStatement, impactStatements, setImpactStatements, stage],
  );

  if (availableStages && availableStages.length === 1) {
    const onlyStage = availableStages[0];
    return (
      <ImpactStatementView
        stage={onlyStage}
        statementDefinition={statementDefinition}
        impactStatement={impactStatements[onlyStage]}
        previousStatement={
          impactStatements[impactStageDefinitions[onlyStage].previousStage]
        }
        updateImpactStatement={updateImpactStatement}
        createImpactStatement={(statement) =>
          createImpactStatement(statement, onlyStage)
        }
        isCreating={isCreatingStage === onlyStage}
        editable={editable}
        rangeInputType={impactStageDefinitions[onlyStage].rangeInputType}
      />
    );
  }

  return (
    <Tab
      activeIndex={impactStageOrder.indexOf(stage)}
      onTabChange={(_, data) => {
        const newStage = impactStageOrder[data.activeIndex as number];
        setStage(newStage);
      }}
      menu={{ secondary: true, pointing: true }}
      panes={impactStageOrder
        .filter((s) => !availableStages || availableStages.includes(s))
        .map((s) => ({
          menuItem: impactStageDefinitions[s].name,
          render: () => {
            const canEditStage =
              editable || (canManageIdea && s !== "userEstimate");
            const previousStatement =
              impactStatements[impactStageDefinitions[s].previousStage];

            if (s !== "userEstimate" && !impactStatements[s]) {
              return (
                <StageSegment
                  stage={s}
                  statementDefinition={statementDefinition}
                  impactStatement={undefined}
                  onCreate={() => {
                    createImpactStatement(
                      {
                        impacts: previousStatement?.impacts ?? {},
                        description: previousStatement?.description,
                      },
                      s,
                    );
                  }}
                  isCreating={isCreatingStage === s}
                />
              );
            }
            return (
              <ImpactStatementView
                stage={s}
                statementDefinition={statementDefinition}
                impactStatement={impactStatements[s]}
                previousStatement={canEditStage ? previousStatement : undefined}
                updateImpactStatement={updateImpactStatement}
                createImpactStatement={(statement) =>
                  createImpactStatement(statement, s)
                }
                isCreating={isCreatingStage === s}
                editable={canEditStage}
                rangeInputType={impactStageDefinitions[s].rangeInputType}
              />
            );
          },
        }))}
    />
  );
};
